/**
 * Shared AJAX cart-add helper.
 *
 * Problem it solves: every custom ATC path in the theme (FBT, post-ATC upsell,
 * product-finder results, cart add-on, sticky ATC, etc.) used to fire bare
 * /cart/add.js calls without the `sections` parameter. Shopify's Section
 * Rendering API requires that parameter to return pre-rendered HTML for the
 * cart drawer + cart bubble. Without it, `component-cart-items.js` can't morph
 * the drawer DOM — so the drawer opens on stale empty state and the cart
 * bubble doesn't update.
 *
 * Use this helper from every ATC path so the cart UI updates correctly:
 *
 *   window.HE.cartAdd([{ id: variantId, quantity: 1 }], { source: 'my-button' });
 *
 * Returns a Promise resolving to the cart JSON. Dispatches a canonical
 * `cart:update` event with detail.data.sections populated so drawer / bubble
 * refresh normally.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.HE && typeof window.HE.cartAdd === 'function') return;

  /**
   * Find every cart-items-component on the page and collect their section IDs.
   * These are the sections that need fresh HTML from Shopify on each cart change.
   */
  function collectSectionIds() {
    var ids = new Set();
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
    });
    return Array.from(ids);
  }

  async function cartAdd(items, options) {
    options = options || {};
    var source = options.source || 'he-cart-add';
    var normalized = Array.isArray(items) ? items : [items];

    var sectionIds = collectSectionIds();
    var body = { items: normalized };
    if (sectionIds.length > 0) {
      body.sections = sectionIds.join(',');
      body.sections_url = window.location.pathname;
    }
    if (options.attributes) {
      body.attributes = options.attributes;
    }

    var response = await fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    var data = await response.json().catch(function () { return null; });

    // Shopify cart errors return status 422 + { status, message, description }
    if (!response.ok || (data && data.status)) {
      var err = new Error((data && (data.description || data.message)) || 'Cart add failed');
      err.cartData = data;
      err.httpStatus = response.status;
      throw err;
    }

    // Dispatch the canonical cart:update event with the full detail payload.
    // This is the shape component-cart-items.js expects: detail.data.sections is required.
    var eventDetail = {
      resource: data,
      sourceId: source,
      data: {
        source: source,
        sections: (data && data.sections) || {},
        itemCount: data && typeof data.item_count === 'number' ? data.item_count : undefined,
        productId: data && data.product_id,
        variantId: data && data.variant_id,
      },
    };

    document.dispatchEvent(
      new CustomEvent('cart:update', { bubbles: true, detail: eventDetail })
    );
    document.dispatchEvent(
      new CustomEvent('cart:added', { bubbles: true, detail: eventDetail })
    );

    return data;
  }

  /**
   * Change or remove a line item from the cart (wrapper around /cart/change.js
   * that includes sections so the drawer updates). Used by cart-add-on toggle
   * when removing an item.
   */
  async function cartChange(body) {
    var sectionIds = collectSectionIds();
    var payload = Object.assign({}, body || {});
    if (sectionIds.length > 0) {
      payload.sections = sectionIds.join(',');
      payload.sections_url = window.location.pathname;
    }
    var response = await fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(payload),
    });
    var data = await response.json().catch(function () { return null; });
    if (!response.ok || (data && data.status)) {
      var err = new Error((data && (data.description || data.message)) || 'Cart change failed');
      err.cartData = data;
      throw err;
    }
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: 'he-cart-change',
          data: { source: 'he-cart-change', sections: (data && data.sections) || {} },
        },
      })
    );
    return data;
  }

  window.HE = window.HE || {};
  window.HE.cartAdd = cartAdd;
  window.HE.cartChange = cartChange;
})();
